import { api } from "../scripts/api.js";

class CustomeSelect {
   static _instance = new Set();
   static esc(s) {
      return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
   }

   constructor(mount) {
      this.mount = mount;
      this.value = "";
      this._options = [];
      this._open = false;
      this._handlers = [];
      CustomeSelect._instance.add(this);

      mount.classList.add("cs");
      mount.innerHTML = `
         <button type="button" class="cs-trigger" aria-haspopup="listbox" aria-expanded="false">
            <span class="cs-label"></span>
            <svg class="cs-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
               <path d="M6 9l6 6 6-6"/>
            </svg>
         </button>
      `;
      this.trigger = mount.querySelector(".cs-trigger");
      this.labelEl = mount.querySelector(".cs-label");
      this.menu = null;

      this.trigger.addEventListener("click", (e) => {
         e.stopPropagation();
         this.toggle();
      });

      this._onDoc = (e) => {
         if (this.mount.contains(e.target)) return;
         if (this.menu && this.menu.contains(e.target)) return;
         this.close();
      };

      this._onReposition = (e) => {
         // Scrolling inside the menu itself must not re-run position().
         if (e && e.type === "scroll" && this.menu && this.menu.contains(e.target)) return;
         if (this._open) this.position();
      };

      document.addEventListener("click", this._onDoc);
      window.addEventListener("resize", this._onReposition);
      window.addEventListener("scroll", this._onReposition, true);
   }

   set innerHTML(html) {
      const tmp = document.createElement("select");
      tmp.innerHTML = html;
      this._options = Array.from(tmp.options).map((o) => ({ value: o.value, label: o.textContent }));
      if (this._options.length) this.setValue(this._options[0].value, false);
      else { this.value = ""; this.labelEl.textContent = ""; }
      if (this._open) this.renderMenu();
   }

   setValue(v, fire) {
      this.value = v;
      const opt = this._options.find((o) => o.value === v);
      this.labelEl.textContent = opt ? opt.label : "";
      this.trigger.title = opt ? opt.label : "";
      if (this.menu) {
         this.menu.querySelectorAll("li").forEach((li) => {
            li.classList.toggle("is-selected", li.dataset.value === v);
         });
      }
      if (fire) this._emit();
   }

   addEventListener(type, fn) {
      if (type === "change") this._handlers.push(fn);
   }
   _emit() {
      this._handlers.forEach((fn) => fn({ target: this }));
   }

   toggle() {
      this._open ? this.close() : this.open();
   }

   open() {
      if (this._open) return;
      CustomeSelect._instance.forEach((cs) => {
         if (cs !== this) cs.close();
      });
      this._open = true;
      this.renderMenu();
      this.mount.classList.add("is-open");
      this.trigger.setAttribute("aria-expanded", "true");
   }

   renderMenu() {
      this.destroyMenu();
      const menu = document.createElement("ul");
      menu.className = "cs-menu";
      menu.setAttribute("role", "listbox");
      menu.innerHTML = this._options
         .map((o) => `<li role="option" data-value="${CustomeSelect.esc(o.value)}" class="${o.value === this.value ? "is-selected" : ""}" title="${CustomeSelect.esc(o.label)}"><span class="cs-check">✓</span><span class="cs-opt-label">${CustomeSelect.esc(o.label)}</span></li>`)         .join("");
      menu.addEventListener("click", (e) => {
         const li = e.target.closest("li[data-value]");
         if (!li) return;
         this.setValue(li.dataset.value, true);
         this.close();
      });
      document.body.appendChild(menu);
      this.menu = menu;
      this.position();
      // Long lists scroll inside the menu; start with the current selection in view.
      const sel = menu.querySelector("li.is-selected");
      if (sel) menu.scrollTop = Math.max(0, sel.offsetTop - (menu.clientHeight - sel.offsetHeight) / 2);
   }

   // Keeps the menu inside the viewport. Before 2026-09-10 the menu had no
   // max-height, so a class with many assignments ran off the bottom of the
   // window and the lower ones could not be reached (the menu is
   // position:fixed, so scrolling the page never brought them into view).
   position() {
      const menu = this.menu;
      if (!menu) return;
      const GAP = 6;      // space between trigger and menu
      const MARGIN = 12;  // minimum space from any viewport edge
      const MIN_H = 160;  // never squeeze the menu below this
      const r = this.trigger.getBoundingClientRect();
      const dx = this.mount.id === "assignment-select" ? -12 : -30;
      const vw = document.documentElement.clientWidth;
      const vh = window.innerHeight;

      menu.style.minWidth = `${Math.min(r.width, vw - 2 * MARGIN)}px`;
      menu.style.maxWidth = `${vw - 2 * MARGIN}px`;

      // Full content height, even while clipped (scrollHeight + borders).
      const natural = menu.scrollHeight + (menu.offsetHeight - menu.clientHeight);
      const below = vh - r.bottom - GAP - MARGIN;
      const above = r.top - GAP - MARGIN;
      const openUp = natural > below && above > below;
      const avail = Math.max(MIN_H, openUp ? above : below);
      const h = Math.min(natural, avail);
      menu.style.maxHeight = `${avail}px`;
      menu.style.top = `${openUp ? Math.max(MARGIN, r.top - GAP - h) : r.bottom + GAP}px`;

      const w = menu.offsetWidth;
      const left = Math.max(MARGIN, Math.min(r.left + dx, vw - MARGIN - w));
      menu.style.left = `${left}px`;
   }

   destroyMenu() {
      if (this.menu) {
         this.menu.remove();
         this.menu = null;
      }
   }

   close() {
      if (!this._open) return;
      this._open = false;
      this.destroyMenu();
      this.mount.classList.remove("is-open");
      this.trigger.setAttribute("aria-expanded", "false");
   }

   destroy() {
      this.close();
      CustomeSelect._instance.delete(this);
      document.removeEventListener("click", this._onDoc);
      window.removeEventListener("resize", this._onReposition);
      window.removeEventListener("scroll", this._onReposition, true);
   }
}

class Scoreboard extends HTMLElement {
   FILTER_LABELS = {
      done: "done",
      inProgress: "in-progress",
      stuck: "stuck",
      notStarted: "not started",
   };

   connectedCallback() {
      this.innerHTML = `
         <section class="scoreboard">
            <header class="scoreboard-header">
               <div>
                  <h1>Scoreboard</h1>
                  <span id="live-pill" class="live-pill" hidden>
                     <span class="live-dot"></span><span id="live-text">Live</span>
                  </span>
                  <!-- Game clock (2026-10-15): only while a game on this class + assignment is live. -->
                  <div id="sb-game-clock" class="sb-game-clock" hidden aria-live="off"></div>
                  <p id="scoreboard-sub" class="scoreboard-sub"></p>
                  <div id="scoreboard-summary" class="scoreboard-summary" hidden></div>
               </div>
               <div class="scoreboard-filter">
                  <div class="filter-pill">
                     <svg class="filter-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
                     </svg>
                     <span class="filter-label">Class:</span>
                     <div class="cs" id="class-select"></div>
                  </div>
                  <div class="filter-pill">
                     <svg class="filter-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h8M8 9h2"/>
                     </svg>
                     <span class="filter-label">Assignment:</span>
                     <div class="cs" id="assignment-select"></div>
                  </div>
                  <label class="reveal-toggle" title="Off by default so the board is safe to project. Resets on reload.">
                     <input type="checkbox" id="reveal-names"><span>Show real names</span>
                  </label>
                  <button type="button" id="gen-link-btn" class="gen-link-btn">Generate overlay link</button>
               </div>
            </header>

            <div class="scoreboard-view-toggle" role="group" aria-label="View mode">
               <button type="button" id="view-per-level" class="toggle-btn is-active" aria-pressed="true" data-mode="level">Per-level</button>
               <button type="button" id="view-per-question" class="toggle-btn" aria-pressed="false" data-mode="question">Per-question</button>
            </div>

            <div class="scoreboard-table-wrap">
               <table class="scoreboard-table">
                  <thead><tr id="scoreboard-head"></tr></thead>
                  <tbody id="scoreboard-body"></tbody>
               </table>
            </div>

            <p id="scoreboard-empty" class="empty" hidden>No submission yet.</p>

            <dialog id="overlay-dialog" class="overlay-dialog">
               <form method="dialog" class="od-form">
                  <header class="od-head">
                     <h2>OBS overlay link</h2>
                     <button value="cancel" class="od-close" aria-label="Close">&times;</button>
                  </header>
                  <p class="od-desc">Public, read-only board for OBS. Shows nicknames or team names only (never real names or emails). Drop the URL into OBS as a Browser source.</p>

                  <div class="od-row">
                     <span class="od-label">Layout</span>
                     <div class="od-seg" id="od-version" role="group" aria-label="Layout version">
                        <button type="button" data-v="list">List</button>
                        <button type="button" data-v="columns">Columns</button>
                        <button type="button" data-v="compact">Compact</button>
                        <button type="button" data-v="team-grid">Team grid</button>
                     </div>
                     <span id="od-suggest" class="od-hint"></span>
                  </div>

                  <div class="od-row">
                     <span class="od-label">View</span>
                     <div class="od-seg" id="od-view" role="group" aria-label="View mode">
                        <button type="button" data-view="level">Per-level</button>
                        <button type="button" data-view="question">Per-question</button>
                     </div>
                  </div>

                  <div class="od-row">
                     <span class="od-label">Background</span>
                     <div class="od-seg" id="od-bg" role="group" aria-label="Background">
                        <button type="button" data-bg="transparent">Transparent</button>
                        <button type="button" data-bg="dark">Dark</button>
                        <button type="button" data-bg="light">Light</button>
                     </div>
                  </div>

                  <div class="od-row">
                     <span class="od-label">Game clock</span>
                     <div class="od-seg" id="od-clock" role="group" aria-label="Game clock">
                        <button type="button" data-clock="off">Off</button>
                        <button type="button" data-clock="top">Above board</button>
                        <button type="button" data-clock="only">Clock only</button>
                     </div>
                     <span id="od-clock-hint" class="od-hint"></span>
                  </div>

                  <div class="od-row">
                     <span class="od-label">Size</span>
                     <div class="od-seg" id="od-size" role="group" aria-label="Overlay size">
                        <button type="button" data-size="auto">Auto</button>
                        <button type="button" data-size="1080">Fixed 1920&times;1080</button>
                     </div>
                     <span id="od-size-hint" class="od-hint"></span>
                  </div>

                  <div class="od-url-row">
                     <input id="od-url" class="od-url" readonly aria-label="Overlay URL" />
                     <button type="button" id="od-copy" class="od-copy">Copy URL</button>
                  </div>

                  <div class="od-preview-wrap">
                     <iframe id="od-preview" class="od-preview" title="Overlay preview"></iframe>
                  </div>

                  <footer class="od-foot">
                     <button type="button" id="od-regen" class="od-regen">Regenerate link</button>
                     <span class="od-hint">Regenerating stops the old link from working.</span>
                  </footer>
               </form>
            </dialog>
         </section>
      `;

      this.classSelect = new CustomeSelect(this.querySelector("#class-select"));
      this.select = new CustomeSelect(this.querySelector("#assignment-select"));
      this.head = this.querySelector("#scoreboard-head");
      this.body = this.querySelector("#scoreboard-body");
      this.empty = this.querySelector("#scoreboard-empty");
      this.livePill = this.querySelector("#live-pill");
      this.liveText = this.querySelector("#live-text");
      this.sub = this.querySelector("#scoreboard-sub");
      this.summary = this.querySelector("#scoreboard-summary");

      this.currentId = null;
      this.lastLoaded = null;
      this._filter = null;    // null = show all

      this.classSelect.addEventListener("change", () => this.loadAssignments(this.classSelect.value));
      this.select.addEventListener("change", () => {
         this._gen = (this._gen ?? 0) + 1;
         this.clearBoard();
         this.load(this.select.value);
      });
      
      this._view = "level";

      const toggle = this.querySelector(".scoreboard-view-toggle");
      toggle.addEventListener("click", (e) => {
         const btn = e.target.closest(".toggle-btn");
         if (!btn || btn.dataset.mode === this._view) return;

         this._view = btn.dataset.mode;

         toggle.querySelectorAll(".toggle-btn").forEach((b) => {
            const active = b === btn;
            b.classList.toggle("is-active", active);
            b.setAttribute("aria-pressed", String(active));
         });
         // re-render with the last loaded data
         if (this.lastRows) this.rerender();
      });

      this._openRow = null;
      this._openLevel = null;

      this.body.addEventListener("click", (e) => {
         if (this._view !== "level") return;
         const cell = e.target.closest("td.col-level");
         if (!cell) return;
         const tr = cell.closest("tr");
         const uid = Number(tr?.dataset.uid);
         const li = Number(cell.dataset.li);
         if (Number.isNaN(uid) || Number.isNaN(li)) return;

         // toggle: same cell closes, different cell moves
         if (this._openRow === uid && this._openLevel === li) {
            this._openRow = this._openLevel = null;
         } else {
            this._openRow = uid;
            this._openLevel = li;
         }
         if (this.lastRows) this.rerender();
      });

      this._onDocClick = (e) => {
         // reset bucket filter if a filter is active and the click missed the chips
         if (this._filter && !e.target.closest(".sm-item[data-bucket]") && !e.target.closest(".scoreboard-table-wrap")) {
            this._filter = null;
            if (this.lastRows) this.rerender();
         }

         // close drill-down when clicking anywehere outside a level cell
         if (this._openRow == null) return;
         if (e.target.closest("td.col-level")) return;
         if (e.target.closest(".dd-row")) return;
         this._openRow = this._openLevel = null;
         if (this.lastRows) this.rerender();
      };
      document.addEventListener("click", this._onDocClick);

      this.summary.addEventListener("click", (e) => {
         const chip = e.target.closest(".sm-item[data-bucket]");
         if (!chip) return;
         const bucket = chip.dataset.bucket;
         this._filter = (this._filter === bucket) ? null : bucket;
         if (this.lastRows) this.rerender();
      });

      this.setupLinkDialog();

      // Kept so show() can wait for the first class/assignment load before
      // applying a deep link (the router calls show() as soon as the view mounts).
      this._ready = this.init();

      // poll every seconds
      this.poll = setInterval(() => { if (this.currentId) this.load(this.currentId, true); }, 3000);
      this.tick = setInterval(() => { this.updateLiveLabel(); this.drawGameClock(); }, 1000);

      this._onHashChange = () => {
         if (location.hash.replace(/^#/, "") === "scoreboard") {
            this.refreshOnEnter();
         }
      };
      window.addEventListener("hashchange", this._onHashChange);
   }

   disconnectedCallback() {
      clearInterval(this.poll);
      clearInterval(this.tick);
      document.removeEventListener("click", this._onDocClick);
      window.removeEventListener("hashchange", this._onHashChange);
      this.classSelect?.destroy();
      this.select?.destroy();
   }

   // ---- OBS overlay "Generate link" dialog ---------------------------------
   // Grafted onto Phong's authed board. Reads this.classSelect.value /
   // this.select.value (CustomeSelect exposes .value), this.lastRows, this._view,
   // and this.lastSourceMode. A team board (source_mode='team') defaults to the
   // team-grid layout; everything else picks from the head-count.
   setupLinkDialog() {
      this.dialog = this.querySelector("#overlay-dialog");
      this.urlInput = this.querySelector("#od-url");
      this.preview = this.querySelector("#od-preview");
      this.suggestEl = this.querySelector("#od-suggest");

      // dialog state (separate from the board's own view toggle)
      this._link = { token: null, v: "list", view: "level", bg: "transparent", clock: "off", size: "auto" };

      this.querySelector("#gen-link-btn").addEventListener("click", () => this.openLinkDialog());

      /*
         Real names are OFF by default because this board gets projected. The
         state lives ONLY on this element - not localStorage, not a saved class
         setting - so a reload puts it back to nicknames and it can never be
         left switched on for the next class. Reloads the board immediately so
         the change is visible rather than waiting for the next poll.
      */
      this.reveal = false;
      this.querySelector("#reveal-names").addEventListener("change", (e) => {
         this.reveal = e.target.checked;
         this.clearBoard();
         if (this.select.value) this.load(this.select.value);
      });
      this.querySelector(".od-close").addEventListener("click", () => this.dialog.close());

      // segmented toggles: layout / view / background
      this.querySelector("#od-version").addEventListener("click", (e) => this.pickSeg(e, "v", "v"));
      this.querySelector("#od-view").addEventListener("click", (e) => this.pickSeg(e, "view", "view"));
      this.querySelector("#od-bg").addEventListener("click", (e) => this.pickSeg(e, "bg", "bg"));
      this.querySelector("#od-clock").addEventListener("click", (e) => this.pickSeg(e, "clock", "clock"));
      this.querySelector("#od-size").addEventListener("click", (e) => this.pickSeg(e, "size", "size"));

      this.querySelector("#od-copy").addEventListener("click", () => this.copyOverlayUrl());
      this.querySelector("#od-regen").addEventListener("click", () => this.mintLink(true));
   }

   async openLinkDialog() {
      const classId = this.classSelect.value;
      const assignmentId = this.select.value;
      if (!classId || !assignmentId) {
         alert("Pick a class and assignment first.");
         return;
      }

      // team boards default to team-grid; otherwise suggest from the head-count
      const n = this.lastRows?.length ?? 0;
      this._link.v = this.lastSourceMode === "team" ? "team-grid" : this.suggestVersion(n);
      this._link.view = this._view;           // match the board's current view
      this._link.bg = "transparent";
      // A game's countdown is on by default when this assignment has a game in
      // this class; otherwise off (the overlay would have no clock to show).
      const hasGame = this._gameAsgIds?.has(String(assignmentId));
      this._link.clock = hasGame ? "top" : "off";
      // Size: Auto (the board sizes to its content) unless picked; Fixed adds
      // &fixed=1080 (the 1920x1080 broadcast layout, see scripts/overlay.js).
      this._link.size = "auto";
      const clockHint = this.querySelector("#od-clock-hint");
      if (clockHint) clockHint.textContent = hasGame
         ? "Follows the game on this class and assignment."
         : "No game on this class and assignment yet; the clock appears once you create one.";
      this.suggestEl.textContent = n ? `Suggested for ${n} ${this.lastSourceMode === "team" ? "team" : "player"}${n === 1 ? "" : "s"}` : "";

      this.syncSegs();
      const ok = await this.mintLink(false);
      if (ok && !this.dialog.open) this.dialog.showModal();
      this.fitPreview();   // the preview's width is only known once the dialog is open
   }

   suggestVersion(n) {
      if (n <= 15) return "list";
      if (n <= 40) return "columns";
      return "compact";
   }

   // Mint (or regenerate) a token for the current class+assignment, then refresh
   // the URL + preview. `regen` just adds the revoke warning + confirm.
   async mintLink(regen) {
      const classId = Number(this.classSelect.value);
      const assignmentId = Number(this.select.value);
      if (regen && !confirm("Regenerate the link? The current overlay URL will stop working.")) {
         return false;
      }
      try {
         /* reuse on open, mint only on Regenerate (2026-09-29). Opening this
            dialog used to mint, and minting revokes, so just LOOKING at the link
            broke the URL already pasted into OBS. The server returns the live
            token for this pair when reuse is set, and mints only if none exists. */
         const res = await api("/scoreboard/link", {
            method: "POST",
            body: JSON.stringify({ class_id: classId, assignment_id: assignmentId, reuse: !regen }),
         });
         if (!res.ok) {
            const e = new Error(`scoreboard ${res.status}`);
            e.status = res.status;
            throw e;
         }
         const data = await res.json();
         this._link.token = data.token;
         this.refreshOverlayUrl();
         return true;
      } catch (err) {
         console.error("mint overlay link failed:", err);
         alert("Could not generate the overlay link. Check that you are signed in as this class's professor.");
         return false;
      }
   }

   overlayUrl() {
      const url = new URL(`${location.origin}/overlay.html`);
      url.searchParams.set("token", this._link.token);
      url.searchParams.set("v", this._link.v);
      url.searchParams.set("view", this._link.view);
      url.searchParams.set("bg", this._link.bg);
      if (this._link.clock && this._link.clock !== "off") url.searchParams.set("clock", this._link.clock);
      if (this.isFixedLink()) url.searchParams.set("fixed", "1080");
      return url.toString();
   }

   // Fixed 1920x1080 applies to the board; a Clock only link ignores it
   // (overlay.js does the same), so the URL never carries it there.
   isFixedLink() {
      return this._link.size === "1080" && this._link.clock !== "only";
   }

   /* A fixed link is a 1920x1080 page, so the preview renders it at full size
      and scales it down to the preview's width (16:9) instead of showing its
      top-left corner. It stays transparent over the checker, which shows
      exactly which areas a background behind it would show through. */
   fitPreview() {
      const wrap = this.preview.parentElement;
      const fixed = this.isFixedLink();
      wrap.classList.toggle("is-fixed", fixed);
      const w = wrap.clientWidth;
      this.preview.style.transform = fixed && w ? `scale(${w / 1920})` : "";
   }

   refreshOverlayUrl() {
      if (!this._link.token) return;
      const url = this.overlayUrl();
      this.urlInput.value = url;
      // preview on a neutral backdrop so a transparent overlay is still visible
      // (not for a fixed link: its checker preview shows the transparent areas)
      const previewUrl = this.isFixedLink() ? url : url.replace(/([?&])bg=transparent/, "$1bg=dark");
      if (this.preview.src !== previewUrl) this.preview.src = previewUrl;
      this.fitPreview();
   }

   pickSeg(e, key, attr) {
      const btn = e.target.closest(`button[data-${attr}]`);
      if (!btn || btn.disabled) return;
      this._link[key] = btn.dataset[attr];
      this.syncSegs();
      this.refreshOverlayUrl();
   }

   syncSegs() {
      const mark = (sel, attr, val) =>
         this.querySelectorAll(`${sel} button`).forEach((b) =>
            b.classList.toggle("is-active", b.dataset[attr] === val)
         );
      mark("#od-version", "v", this._link.v);
      mark("#od-view", "view", this._link.view);
      mark("#od-bg", "bg", this._link.bg);
      mark("#od-clock", "clock", this._link.clock);
      mark("#od-size", "size", this._link.size);

      // Fixed ignores the layout (always the list table), and Clock only ignores
      // the size, so grey out whichever does not apply and say why.
      const fixed = this.isFixedLink();
      const clockOnly = this._link.clock === "only";
      this.querySelectorAll("#od-version button").forEach((b) => { b.disabled = fixed; });
      this.querySelectorAll("#od-size button").forEach((b) => { b.disabled = clockOnly; });
      if (this.suggestEl) this.suggestEl.hidden = fixed;
      const sizeHint = this.querySelector("#od-size-hint");
      if (sizeHint) sizeHint.textContent = clockOnly
         ? "Not used with Clock only."
         : fixed
            ? "Always 10 rows on a 1920×1080 page; Layout is ignored. Set the OBS source to 1920 × 1080."
            : "";
   }

   async copyOverlayUrl() {
      const url = this.urlInput.value;
      if (!url) return;
      const copyBtn = this.querySelector("#od-copy");
      try {
         await navigator.clipboard.writeText(url);
      } catch {
         this.urlInput.select();          // fallback: select for manual Ctrl+C
         try { document.execCommand("copy"); } catch { /* leave selected */ }
      }
      const prev = copyBtn.textContent;
      copyBtn.textContent = "Copied";
      setTimeout(() => { copyBtn.textContent = prev; }, 1500);
   }

   async init() {
      try {
         const res = await api("/list-classes");
         if (!res.ok) {
            const e = new Error(`scoreboard ${res.status}`);
            e.status = res.status;
            throw e;
         }
         const data = await res.json();
         const classes = data.classes ?? [];
         this.classSelect.innerHTML = classes
            .map((c) => `<option value="${c.class_id}">${this.esc(`${c.course_code} · ${c.section}`)}</option>`)
            .join("");
         if (classes.length) {
            await this.loadAssignments(classes[0].class_id);
         } else {
            this.setStatus("No classes yet.");
         }
      } catch (err) {
         this.setStatus("Could not load classes.");
         console.error(err);
      }
   }

   async loadAssignments(classId) {
      this._gen = (this._gen ?? 0) + 1;
      const gen = this._gen;

      // reset assignment-dependent state so stale data doesn't keep polling
      this.currentId = null;
      this.clearBoard();

      try {
         const list = await this.assignmentOptions(classId);
         if (gen !== this._gen) return;
         this.select.innerHTML = list
            .map((a) => `<option value="${a.assignment_id}">${this.esc(a.title ?? a.assignment_id)}</option>`)
            .join("");
         if (list.length) {
            this.load(list[0].assignment_id);
         } else {
            this.clearBoard();
            this.setStatus("No assignments for this class yet.", "ok");
         }
      } catch (err) {
         if (gen !== this._gen) return;
         this.setStatus("Could not load assignments.");
         console.error(err);
      }
   }

   async refreshOnEnter() {
      const prevClasses = this.classSelect.value;
      const prevAsg = this.select.value;

      try {
         // refresh class list
         const res = await api('/list-classes');
         if (!res.ok) return;
         const data = await res.json();
         const classes = data.classes ?? [];

         this.classSelect.innerHTML = classes
            .map((c) => `<option value="${c.class_id}">${this.esc(`${c.course_code} · ${c.section}`)}</option>`)
            .join("");
         
         // keep the class the user was on if it still exists
         const classStillThere = classes.some((c) => String(c.class_id) === String(prevClasses));
         if (!classes.length) {
            this.clearBoard();
            this.setStatus("No classes yet.");
            return;
         }
         const targetClass = classStillThere ? prevClasses : classes[0].class_id;
         if (targetClass == null || targetClass === "") return;

         this.classSelect.setValue(targetClass, false);

         // refresh the assignment dropdown for the class, keeping the selection
         await this.loadAssignmentsPreserving(targetClass, classStillThere ? prevAsg : null);
      } catch (err) {
         console.error("scoreboard re-entry refresh failed:", err);
      }
   }

   async loadAssignmentsPreserving(classId, keepAsgId) {
      this._gen = (this._gen ?? 0) + 1;
      const gen = this._gen;
      this.currentId = null;

      try {
         const list = await this.assignmentOptions(classId);
         if (gen !== this._gen) return;
         this.select.innerHTML = list
            .map((a) => `<option value="${a.assignment_id}">${this.esc(a.title ?? a.assignment_id)}</option>`)
            .join("");
         
         if (!list.length) {
            this.clearBoard();
            this.setStatus("No assignments for this class yet.", "ok");
            return;
         }

         // keep the previously selected assignment if it survived the change
         const keep = keepAsgId != null && list.some((a) => String(a.assignment_id) === String(keepAsgId));
         const target = String(keep ? keepAsgId : list[0].assignment_id);
         this.select.setValue(target, false);
         await this.load(target);
      } catch (err) {
         this.setStatus("Could not load assignments.");
         console.error(err);
      }
   }

   /*
   The assignment dropdown for a class (2026-09-29): the class's attached
   assignments PLUS any assignment that has a GAME in this class.

   A game can be created on ANY assignment (the New game dialog lists them all),
   and the case for a game is often deliberately NOT attached to the class so
   students cannot open it before the game. The dropdown used to list attached
   assignments only, so a live game's board was unreachable from this tab even
   though the route behind it has never required the attachment.

   Game assignments go FIRST, newest game first, marked "(game)". /games is
   instructor-only; for anyone it refuses, the list is simply the attached set.
   */
   async assignmentOptions(classId) {
      const res = await api(`/classes/${encodeURIComponent(classId)}`);
      if (!res.ok) {
         const e = new Error(`scoreboard ${res.status}`);
         e.status = res.status;
         throw e;
      }
      const data = await res.json();
      const attached = data.assignments ?? [];

      let games = [];
      try {
         const gr = await api(`/games?class_id=${encodeURIComponent(classId)}`);
         if (gr.ok) games = (await gr.json()).games ?? [];
      } catch { /* no game list is not an error for this view */ }

      this._gameAsgIds = new Set(games.map((g) => String(g.assignment_id)));
      const seen = new Set();
      const out = [];
      for (const g of games) {
         const id = String(g.assignment_id);
         if (seen.has(id)) continue;
         seen.add(id);
         out.push({ assignment_id: id, title: `${g.assignment_title ?? g.title ?? id} (game)` });
      }
      for (const a of attached) {
         const id = String(a.assignment_id);
         if (seen.has(id)) continue;
         seen.add(id);
         out.push({ ...a, assignment_id: id });
      }
      return out;
   }

   /*
   Deep link from the Games tab: #scoreboard/<classId>/<assignmentId>[/obs].
   Selects that class and assignment and, with /obs, opens the overlay dialog.
   The hash is then put back to plain #scoreboard WITHOUT a hashchange, so a
   reload does not reopen the dialog and the menu still marks Scoreboard active.
   A bare #scoreboard is handled by refreshOnEnter via the hashchange listener.
   */
   async show(param) {
      if (!param) return;
      const [cls, asg, flag] = String(param).split("/");
      if (!cls || !asg) return;
      try { await this._ready; } catch { /* init reported its own error */ }
      history.replaceState(null, "", `${location.pathname}${location.search}#scoreboard`);
      this.classSelect.setValue(String(cls), false);
      await this.loadAssignmentsPreserving(cls, asg);
      if (String(this.select.value) !== String(asg)) {
         this.setStatus("That game's assignment could not be found for this class.");
         return;
      }
      if (flag === "obs") await this.openLinkDialog();
   }

   clearBoard() {
      this.setGameClock(null);
      this.head.innerHTML = "";
      this.body.innerHTML = "";
      this.sub.innerHTML = "";
      this.querySelector(".scoreboard-table-wrap").hidden = true;
      this.empty.hidden = true;
      this.livePill.hidden = true;
      this.summary.hidden = true;
      this.lastRows = null;
      this.lastLevels = null;
      this.lastLoaded = null;
      this._lastSig = null;
      this._filter = null;
      this._openRow = this._openLevel = null;
      this._failCount = 0;
   }

   async load(assignmentId, silent = false) {
      const classId = this.classSelect.value;
      if (!assignmentId || !classId) return;
      this.currentId = assignmentId;
      const gen = this._gen ?? 0;

      if (!silent) this.setStatus("Loading...", "ok");

      try {
         const res = await api(`/scoreboard?assignment_id=${encodeURIComponent(assignmentId)}&class_id=${encodeURIComponent(classId)}${this.reveal ? "&reveal=1" : ""}`);
         if (!res.ok) {
            const e = new Error(`scoreboard ${res.status}`);
            e.status = res.status;
            throw e;
         }
         const data = await res.json();

         // ignore if the class changed, or the selected assignment moved on
         if (gen !== this._gen || String(assignmentId) !== String(this.currentId)) return;

         // The game clock is read on EVERY poll, before the unchanged-rows shortcut below:
         // the rows can sit still for minutes while the clock must keep correcting itself.
         this.setGameClock(data.game ?? null);

         // live label updates every poll, even  when data is unchanged
         this.lastLoaded = Date.now();
         this._failCount = 0;
         this.setStatus(null);
         this.updateLiveLabel();

         if (data.configured === false) {
            this.clearBoard();
            this.setStatus("No scoreboard configured for this assignment.", "ok");
            this.currentId = null;
            return;
         }

         // skip the DOM rebuild if nothing changed since last poll
         const sig = JSON.stringify(data.rows);
         if (silent && sig === this._lastSig) return;
         this._lastSig = sig;

         this.lastRows = data.rows ?? [];
         this.lastLevels = data.levels ?? [];
         this.lastSourceMode = data.source_mode ?? "individual";
         this.lastMode = data.mode ?? "questions";
         this.livePill.hidden = false;
         // A timed challenge is ranked on best elapsed time and has no per-question
         // grid to draw, so it gets its own renderer rather than an empty one.
         if (this.lastMode === "timed") this.renderTimed(this.lastRows);
         else this.render(this.lastRows, this.lastLevels);
      } catch (err) {
         if (gen !== this._gen || String(assignmentId) !== String(this.currentId)) return;
         if (!silent) this.clearBoard();
         this._lastSig = null;
         this._failCount = (this._failCount ?? 0) + 1;

         if (err.status === 401) {
            this.setStatus("Session expired - sign in again.");
            clearInterval(this.poll);
            this.poll = null;
         } else if (!silent || this._failCount >= 2) {
            this.setStatus("Could not load scoreboard.");
         }
         if (this.liveText) this.liveText.textContent = "Live · reconnecting";
         console.error("scoreboard load failed", { assignmentId, classId, status: err.status }, err);
      }
   }

   /*
   GAME CLOCK (2026-10-15, David: "when i am running a game and i switch to scoreboard
   mode, i don't see the time remaining ... only if it is a current active game").
   GET /scoreboard sends `game` only while a game on this class + assignment is OPEN
   or PAUSED (routes/web/scoreboard-route.js liveGameClock), so a plain or untimed
   assignment never shows one. The server's seconds are the authority on every 3 s
   poll; between polls an open game counts down locally, the same as the OBS overlay.
   */
   setGameClock(game) {
      this._game = game ? { game, at: Date.now() } : null;
      this.drawGameClock();
   }

   drawGameClock() {
      const el = this.querySelector("#sb-game-clock");
      if (!el) return;
      const g = this._game && this._game.game;
      if (!g) { if (!el.hidden) { el.hidden = true; el.innerHTML = ""; } return; }
      let secs = Number(g.remaining_secs ?? 0);
      if (g.state === "open") secs -= (Date.now() - this._game.at) / 1000;
      secs = Math.max(0, Math.ceil(secs));
      const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), sec = secs % 60;
      const t = h ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
      const cls = `sb-game-clock is-${g.state}${g.state === "open" && secs <= 60 ? " is-urgent" : ""}`;
      const html = `<span class="sb-clock-label">${g.state === "paused" ? "Paused" : "Time remaining"}</span>`
         + `<span class="sb-clock-time">${t}</span>`;
      if (el.className !== cls) el.className = cls;
      if (el.innerHTML !== html) el.innerHTML = html;
      el.hidden = false;
   }

   updateLiveLabel() {
      if (!this.liveText) return;
      if (this._failCount >= 2) {
         this.liveText.textContent = "Live · reconnecting";
         return;
      }
      if (!this.lastLoaded) return;
      const secs = Math.round((Date.now() - this.lastLoaded) / 1000);
      const ago = secs < 5 ? "just now" : secs < 60 ? `${secs}s ago` : `${Math.round(secs / 60)}m ago`;
      this.liveText.textContent = `Live · ${ago}`;
   }

   setStatus(msg, kind = "err") {
      if (!msg) {
         this.status?.remove();
         this.status = null;
         return;
      }

      if (!this.status) {
         this.status = document.createElement("p");
         const toggle = this.querySelector(".scoreboard-view-toggle");
         toggle.insertAdjacentElement("afterend", this.status);
      }
      this.status.className = `msg ${kind}`;
      this.status.textContent = msg;
   }

   // One place that knows which renderer the current data wants, so the toggles
   // and the poll cannot disagree about it.
   rerender() {
      if (this.lastMode === "timed") this.renderTimed(this.lastRows);
      else this.render(this.lastRows, this.lastLevels);
   }

   /*
   Leaderboard for a timed challenge: fastest first.

   Deliberately NOT the per-question grid. These assignments grade and time
   themselves in the workbook, so there are no per-question results to colour in;
   what matters is who is quickest and how many runs it took them.

   `best` is the server's own MIN over recorded attempts. `file best` is what the
   student's workbook reported, shown only when it disagrees, since the two use
   slightly different rules and a mismatch is worth seeing rather than hiding.
   */
   renderTimed(rows = []) {
      const fmt = (secs) => {
         if (secs === null || secs === undefined) return "--";
         const n = Number(secs);
         if (!Number.isFinite(n)) return "--";
         if (n < 60) return `${Math.round(n)}s`;
         return `${Math.floor(n / 60)}:${String(Math.round(n % 60)).padStart(2, "0")}`;
      };
      const when = (t) => {
         if (!t) return "--";
         const d = new Date(t);
         return isNaN(d) ? "--" : d.toLocaleString(undefined,
            { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
      };

      this.summary.hidden = true;
      this.sub.innerHTML = "";
      if (!rows.length) {
         this.head.innerHTML = "";
         this.body.innerHTML = "";
         this.querySelector(".scoreboard-table-wrap").hidden = true;
         this.empty.hidden = false;
         // Says what to DO about it. An empty board here almost always means the
         // students have the file but nobody has finished a run yet.
         this.empty.textContent =
            "No runs recorded yet. A run appears here once a student completes the "
            + "challenge with the add-in pane open and signed in.";
         return;
      }
      this.empty.hidden = true;
      this.querySelector(".scoreboard-table-wrap").hidden = false;

      this.head.innerHTML =
         "<tr><th>#</th><th>Student</th><th>Best time</th><th>Attempts</th>"
         + "<th>Last run</th><th>Grade</th></tr>";
      const n = (v) => (v === null || v === undefined) ? null : Number(v);
      this.body.innerHTML = rows.map((r, i) => {
         // The WORKBOOK's numbers are the ones shown and ranked on: it saw every
         // run, we only saw the ones made with the pane open. Ours are the
         // cross-check, not the answer.
         const reported = n(r.reported_best_secs);
         const observed = n(r.observed_best_secs);
         const best = reported ?? observed;
         const attempts = n(r.reported_attempts) ?? n(r.observed_attempts) ?? 0;
         // Flag a real disagreement, not a rounding difference. Usually means the
         // student practised with the pane closed, so we recorded fewer runs than
         // they made, which is expected rather than alarming.
         const drift = (reported !== null && observed !== null
                        && Math.abs(reported - observed) > 1)
            ? ` <span class="sb-drift" title="we recorded ${fmt(observed)} across `
              + `${r.observed_attempts} run(s) seen with the pane open">*</span>`
            : "";
         const grade = (r.grade_pct === null || r.grade_pct === undefined)
            ? "--" : `${Math.round(Number(r.grade_pct) * 100)}%`;
         return `<tr>
            <td>${i + 1}</td>
            <td>${(r.nickname ?? "").replace(/[<>&]/g, "")}</td>
            <td>${fmt(best)}${drift}</td>
            <td>${attempts}</td>
            <td>${when(r.last_run_at)}</td>
            <td>${grade}</td>
         </tr>`;
      }).join("");
   }

   render(rows, levels = []) {
      // this.empty.hidden = rows.length > 0;
      // this.querySelector(".scoreboard-table-wrap").hidden = !rows.length;
      if (!rows.length) {
         this.head.innerHTML = "";
         this.body.innerHTML = "";
         this.summary.hidden = true;
         this.sub.textContent = "";
         return;
      }

      let tableRows = rows;
      // summary strip
      if (rows.length) {
         const s = this.computeSummary(rows);

         this._rankByUid = new Map(rows.map((r, i) => [r.user_id, i + 1]));

         if (this._filter) {
            tableRows = rows.filter((r) => this.bucketOf(r, s.ctx) === this._filter);
         }

         const chip = (key, label, cls) => `<button type="button" class="sm-item ${cls} ${this._filter === key ? "sm-active" : ""}" data-bucket="${key}"><b>${s[key]}</b> ${label}</button>`;

         this.summary.hidden = false;
         this.summary.innerHTML = `
            ${chip("done", "done", "sm-done")}
            ${chip("inProgress", "in-progress", "sm-progress")}
            ${chip("stuck", "stuck", "sm-stuck")}
            ${s.notStarted ? chip("notStarted", "not started", "sm-none") : ""}
            <span class="sm-item sm-avg">avg <b>${s.avg}%</b></span>
         `;
      } else {
         this.summary.hidden = true;
      }

      const levelCount = rows[0]?.levels?.length ?? 0;
      const totalQ = rows[0]?.total_count ?? 0;

      const noun = this.lastSourceMode === "team" ? "teams" : "students";
      this.sub.textContent = rows.length
         ? `${totalQ} questions across ${levelCount} levels · ${rows.length} ${noun}${this._filter ? ` · showing ${tableRows.length} ${this.FILTER_LABELS[this._filter]}` : ""}`
         : "";
      
      this.empty.hidden = tableRows.length > 0;
      this.querySelector(".scoreboard-table-wrap").hidden = !tableRows.length;

      if (this._view === "question") {
         this.renderQuestions(tableRows);
      } else {
         this.renderLevels(tableRows, levels, levelCount);
      }
   }

   renderLevels(rows, levels, levelCount) {
      this.head.innerHTML = `
         <th class="col-ranking">#</th>
         <th class="col-name">Name</th>
         <th class="col-points">Points</th>
         ${Array.from({ length: levelCount }, (_, i) => {
         const range = levels[i]?.range ?? "";
         return `<th class="col-level"><span class="lvl-num">L${i + 1}</span>${range ? `<span class="lvl-range">${range}</span>` : ""}</th>`;
      }).join("")}
      `;

      const colSpan = 3 + levelCount;

      this.body.innerHTML = rows.map((r, i) => {
         // const cells = (r.levels ?? []).map((p, li) =>
         //    `<td class="col-level" data-li="${li}">${this.bar(p)}</td>`
         // ).join("");
         const levelData = Array.from({ length: levelCount }, (_, li) => (r.levels ?? [])[li] ?? 0);
         const cells = levelData.map((p, li) =>
            `<td class="col-level" data-li="${li}">${this.bar(p)}</td>`
         ).join("");

         let rowHtml = this.rowShell(r, i, cells, r.user_id);

         // inject detail row if this row+level is open
         if (this._openRow === r.user_id && this._openLevel != null) {
            const range = levels[this._openLevel]?.range ?? "";
            const qIdx = this.parseRange(range);
            const marks = r.questions ?? [];
            const detail = qIdx.map((qn) => {
               const q = marks[qn - 1];
               const state = (q == null) ? "blank" : (Number(q) === 1) ? "right" : "wrong";
               const cls = state === "right" ? "q-right" : state === "wrong" ? "q-wrong" : "q-blank";
               const mark = state === "right" ? "✓" : state === "wrong" ? "✗" : "";
               return `<span class="dd-item ${cls}"><span class="dd-qn">Q${qn}</span> ${mark}</span>`;
            }).join("");

            rowHtml += `
               <tr class="dd-row">
                  <td colspan="${colSpan}">${detail}</td>
               </tr>
            `;
         }
         return rowHtml;
      }).join("");
   }

   renderQuestions(rows) {
      const src = this.lastRows?.[0] ?? rows[0];
      const qCount = src?.total_count ?? src?.questions?.length ?? 0;

      this.head.innerHTML = `
         <th class="col-ranking">#</th>
         <th class="col-name">Name</th>
         <th class="col-points">Points</th>
         ${Array.from({ length: qCount }, (_, i) =>
            `<th class="col-q">Q${i + 1}</th>`
         ).join("")}
      `;

      this.body.innerHTML = rows.map((r, i) => {
         const marks = r.questions ?? [];
         const padded = Array.from({ length: qCount }, (_, qi) => marks[qi] ?? null);

         const cells = padded.map((q, qi) => {
            const state = (q == null) ? "blank" : (Number(q) === 1) ? "right" : "wrong";
            const cls = state === "right" ? "q-right" : state === "wrong" ? "q-wrong" : "q-blank";
            const mark = state === "right" ? "✓" : state === "wrong" ? "✗" : "";
            return `<td class="col-q"><div class="q-cell ${cls}" data-qi="${qi}">${mark}</div></td>`;}).join("");
         return this.rowShell(r, i, cells);
      }).join("");
   }

   rowShell(r, i, cells, uid) {
      const rank = this._rankByUid?.get(r.user_id) ?? (i + 1);
      const top = rank <= 3;
      const medalColors = ["#baa117", "#6B7280", "#b46409"];      // gold, silver, bronze
      const rankHtml = top
         ? `<span class="medal" style="background:${medalColors[rank - 1]}">${rank}</span>`
         : `<span class="rank-num">${rank}</span>`;
      return `
         <tr class="${top ? "top-row" : ""}" data-uid="${uid ?? r.user_id ?? ""}">
            <td class="col-ranking">${rankHtml}</td>
            <td class="col-name">${this.esc(r.name ?? "-")}</td>
            <td class="col-points">${r.points_earned ?? r.correct_count ?? 0}</td>
            ${cells}
         </tr>
      `;
   }

   parseRange(range) {
      const m = String(range).match(/Q(\d+)(?:-Q?(\d+))?/i);
      if (!m) return [];
      const lo = Number(m[1]);
      const hi = m[2] ? Number(m[2]) : lo;
      const out = [];
      for (let q = lo; q <= hi; q++) {
         out.push(q);
      }
      return out;
   }

   bar(pct) {
      const n = Number(pct) || 0;
      const cls = n >= 100 ? "bar-pass" : n > 0 ? "bar-partial" : "bar-none";
      const lableCol = n > 40 ? "#fff" : n > 0 ? "#712b13" : "#888780";
      return `
         <div class="bar ${cls}">
            <div class="bar-fill" style="width:${n}%"></div>
            <span class="bar-label" style="color:${lableCol}">${n}%</span>
         </div>
      `;
   }

   computeSummary(rows) {
      const PASS = 60;

      // per-student percentages
      const pcts = rows.map((r) => {
         const t = r.total_count || 0;
         return t ? ((r.correct_count || 0) / t) * 100 : 0;
      });

      // mean and standard deviation (one pass each — O(n))
      const n = pcts.length || 1;
      const mean = pcts.reduce((a, b) => a + b, 0) / n;
      const variance = pcts.reduce((s, p) => s + (p - mean) ** 2, 0) / n;
      const stdDev = Math.sqrt(variance);

      // stuck = well below the class, not below a fixed line
      const stuckLine = Math.min(40, mean - stdDev);

      const ctx = { PASS, stuckLine };
      let done = 0, stuck = 0, inProgress = 0, notStarted = 0;
      for (const r of rows) {
         const b = this.bucketOf(r, ctx);
         if (b === "done") done++;
         else if (b === "stuck") stuck++;
         else if (b === "notStarted") notStarted++;
         else inProgress++;
      }

      return { done, inProgress, stuck, notStarted, avg: Math.round(mean), total: rows.length, ctx };
   }

   bucketOf(r, ctx) {
      const total = r.total_count || 0;
      const answered = (r.questions ?? []).filter((m) => m != null).length;
      const pct = total ? Math.round(((r.correct_count || 0) / total) * 100) : 0;

      if (answered === 0) return "notStarted";
      if (pct < ctx.stuckLine) return "stuck";
      if (answered >= total && pct >= ctx.PASS) return "done";
      return "inProgress";
   }

   esc(s) {
      return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
   }
}

customElements.define("app-scoreboard", Scoreboard);
